/**
 * Formatting Context
 * 
 * Provides FormattingService to all components via React Context.
 * Uses default platform configuration (platform config moved to AWS Parameter Store).
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import FormattingService from '../services/FormattingService';

/**
 * Context type
 */
interface FormattingContextType {
  formatter: FormattingService;
}

/**
 * Create context
 */
const FormattingContext = createContext<FormattingContextType | undefined>(undefined);

/**
 * Provider props
 */
interface FormattingProviderProps {
  children: ReactNode;
}

/**
 * Formatting Provider Component
 */
export const FormattingProvider: React.FC<FormattingProviderProps> = ({ children }) => {
  // Create formatter instance with default config (platform config moved to AWS Parameter Store)
  const formatter = useMemo(() => {
    const defaultConfig = {
      companyName: 'CNAPSE',
      platformName: 'Cnapse Cleanroom Intelligence System',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY' as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD',
      timeFormat: '12h' as '12h' | '24h',
      currency: 'USD',
      defaultLanguage: 'en',
      paginationSize: 25,
    };
    
    return new FormattingService(defaultConfig);
  }, []);

  // Update formatter when config changes
  useEffect(() => {
    // Use default platform configuration (platform config moved to AWS Parameter Store)
    const defaultConfig = {
      companyName: 'CNAPSE',
      platformName: 'Cnapse Cleanroom Intelligence System',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY' as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD',
      timeFormat: '12h' as '12h' | '24h',
      currency: 'USD',
      defaultLanguage: 'en',
      paginationSize: 25,
    };
    
    formatter.updateConfig(defaultConfig);
  }, [formatter]);

  const value = useMemo(() => ({ formatter }), [formatter]);

  return (
    <FormattingContext.Provider value={value}>
      {children}
    </FormattingContext.Provider>
  );
};

/**
 * Hook to use formatting service
 */
export const useFormatter = (): FormattingService => {
  const context = useContext(FormattingContext);
  
  if (!context) {
    throw new Error('useFormatter must be used within FormattingProvider');
  }
  
  return context.formatter;
};

/**
 * Hook with convenient formatting methods
 */
export const useFormatting = () => {
  const formatter = useFormatter();

  return {
    // Date/Time
    formatDate: formatter.formatDate.bind(formatter),
    formatTime: formatter.formatTime.bind(formatter),
    formatDateTime: formatter.formatDateTime.bind(formatter),
    formatRelativeTime: formatter.formatRelativeTime.bind(formatter),
    formatRelativeDate: formatter.formatRelativeDate.bind(formatter),
    formatDateForExport: formatter.formatDateForExport.bind(formatter),
    formatDateTimeForExport: formatter.formatDateTimeForExport.bind(formatter),
    
    // Currency
    formatCurrency: formatter.formatCurrency.bind(formatter),
    formatCurrencyAmount: formatter.formatCurrencyAmount.bind(formatter),
    getCurrencySymbol: formatter.getCurrencySymbol.bind(formatter),
    parseCurrency: formatter.parseCurrency.bind(formatter),
    formatCurrencyRange: formatter.formatCurrencyRange.bind(formatter),
    
    // Numbers
    formatNumber: formatter.formatNumber.bind(formatter),
    formatPercentage: formatter.formatPercentage.bind(formatter),
    formatFileSize: formatter.formatFileSize.bind(formatter),
    
    // Locale
    getCurrentLocale: formatter.getCurrentLocale.bind(formatter),
    translate: formatter.translate.bind(formatter),
    
    // Raw formatter access
    formatter,
  };
};

export default FormattingContext;
